export default class AuthService {
  /** @ngInject **/
  constructor(
    $analytics,
    $http,
    $q,
    $state,
    $injector,
    $log,
    ngAuthSettings,
    userSettingsService,
    cacheService,
    promiseCacheService,
    VuexStore,
    VuexMutationTypes
  ) {
    this.$analytics = $analytics;
    this.$http = $http;
    this.$q = $q;
    this.$state = $state;
    this.$injector = $injector;
    this.$log = $log;
    this.ngAuthSettings = ngAuthSettings;
    this.userSettingsService = userSettingsService;
    this.cacheService = cacheService;
    this.promiseCacheService = promiseCacheService;
    this.VuexStore = VuexStore;
    this.VuexMutationTypes = VuexMutationTypes;

    this.serviceBase = ngAuthSettings.apiServiceBaseUri;
    this._authDataCacheKey = 'authService.authorizationData';

  }

  authentication = {
    isAuth: false,
    isSuper: false,
    userName: '',
    useRefreshTokens: false
  };

  externalAuthData = {
    provider: '',
    userName: '',
    externalAccessToken: ''
  };

  saveRegistration(registration) {
    this.logOut();

    return this.$http.post(this.serviceBase + 'account/register', registration);
  }

  #stringToBool(input) {
    return /^true$/i.test(input);
  }

  login(loginData) {
    let data = 'grant_type=password&username=' + encodeURIComponent(loginData.userName) + '&password=' + encodeURIComponent(loginData.password);
    let loginSuccessResponse;

    if (loginData.useRefreshTokens) {
      data = data + '&client_id=' + this.ngAuthSettings.clientId;
    }

    if (loginData.code) {
      data = data + '&code=' + loginData.code;
    }

    return this.$http.post(this.serviceBase + 'token', data, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    })
      .then((response) => {
        loginSuccessResponse = response;
        this.setAuthDataCache({
          token: response.data.access_token,
          isSuper: false,
          userName: loginData.userName,
          refreshToken: loginData.useRefreshTokens ? response.data.refresh_token : '',
          useRefreshTokens: loginData.useRefreshTokens ? true : false,
          mfaEnforceDate: response.data.twoAuthEnforceDate,
          enforceMfa: this.#stringToBool(response.data.enforceTwoAuth)
        });
        this.authentication.isAuth = true;
        this.authentication.userName = loginData.userName;
        this.authentication.useRefreshTokens = loginData.useRefreshTokens;
        this.isISuper().then(() => {
          this.authentication.isSuper = true;
          this.setAuthDataCache({
            token: response.data.access_token,
            isSuper: true,
            userName: loginData.userName,
            refreshToken: loginData.useRefreshTokens ? response.data.refresh_token : '',
            useRefreshTokens: loginData.useRefreshTokens ? true : false,
            mfaEnforceDate: response.data.twoAuthEnforceDate,
            enforceMfa: this.#stringToBool(response.data.enforceTwoAuth)
          });
        });
        this.VuexStore.commit(this.VuexMutationTypes.RESET);
        this.$analytics.setUsername(this.authentication.userName);

        if (window.$crisp) {
          window.$crisp.push(['set', 'user:email', this.authentication.userName]);
        }

        // This is to fetch the userUISettings and save it in the cache
        return this.userSettingsService.getUserSettings();
      })
      .then( () => {
        return loginSuccessResponse;
      });
  }

  logOut() {
    const accountsService = this.$injector.get('accountsService');
    this.cacheService.remove(this._authDataCacheKey);
    this.cacheService.remove('apitoken.userAuthenticated');
    this.userSettingsService.removeUserUISettingsFromCache();

    this.authentication.isAuth = false;
    this.authentication.isSuper = false;
    this.authentication.userName = '';
    this.authentication.useRefreshTokens = false;

    accountsService.resetAccountData();
    this.promiseCacheService.clearAll();
    this.VuexStore.commit(this.VuexMutationTypes.RESET);

    this.$state.go('login');
  }

  fillAuthData() {
    const authData = this.cacheService.get(this._authDataCacheKey);
    if (authData) {
      this.authentication.isAuth = true;
      this.authentication.isSuper = authData.isSuper;
      this.authentication.userName = authData.userName;
      this.authentication.useRefreshTokens = authData.useRefreshTokens;
    }
  }

  refreshToken() {
    const deferred = this.$q.defer();

    const authData = this.cacheService.get(this._authDataCacheKey);

    this.$log.getInstance('authService').info('Refreshingtoken ', authData);

    if (authData && authData.useRefreshTokens) {
      const data = 'grant_type=refresh_token&refresh_token=' + authData.refreshToken + '&client_id=' + this.ngAuthSettings.clientId;
      this.cacheService.remove(this._authDataCacheKey);
      this.$http.post(this.serviceBase + 'token', data, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      }).then((response) => {

        this.setAuthDataCache({
          token: response.data.access_token,
          userName: response.data.userName,
          isSuper: false,
          refreshToken: response.data.refresh_token,
          useRefreshTokens: true
        });

        this.isISuper().then(() => {
          this.authentication.isSuper = true;
          this.setAuthDataCache({
            token: response.data.access_token,
            userName: response.data.userName,
            isSuper: true,
            refreshToken: response.data.refresh_token,
            useRefreshTokens: true
          });
        });

        deferred.resolve(response);
      }, (err) => {
        this.logOut();
        deferred.reject(err);
      });

    } else {
      this.logOut();
      deferred.reject();
    }

    return deferred.promise;
  }

  obtainAccessToken(externalData) {
    var deferred = this.$q.defer();
    this.$http.get(this.serviceBase + 'api/account/ObtainLocalAccessToken', {
      params: {
        provider: externalData.provider,
        externalAccessToken: externalData.externalAccessToken
      }
    })
      .then((response) => {
        this.setAuthDataCache({
          token: response.data.access_token,
          isSuper: false,
          userName: response.data.userName,
          refreshToken: '',
          useRefreshTokens: false
        });

        this.authentication.isAuth = true;
        this.authentication.isSuper = false;
        this.authentication.userName = response.data.userName;
        this.authentication.useRefreshTokens = false;

        this.isISuper().then(() => {
          this.authentication.isSuper = true;
          this.setAuthDataCache({
            token: response.data.access_token,
            isSuper: true,
            userName: response.data.userName,
            refreshToken: '',
            useRefreshTokens: false
          });
        });
      },(err) => {
        this.logOut();
        deferred.reject(err);
      });

    return deferred.promise;

  }

  registerExternal(registerExternalData) {

    const deferred = this.$q.defer();

    this.$http.post(this.serviceBase + 'api/account/registerexternal', registerExternalData).then((response) => {

      this.setAuthDataCache({
        token: response.data.access_token,
        userName: response.data.userName,
        refreshToken: '',
        useRefreshTokens: false,
        settings: { firstCampaign: true }
      });

      this.authentication.isAuth = true;
      this.authentication.isSuper = false;
      this.authentication.userName = response.data.userName;
      this.authentication.useRefreshTokens = false;

      this.isISuper().then(() => {
        this.authentication.isSuper = true;
        this.setAuthDataCache({
          token: response.data.access_token,
          isSuper: true,
          userName: response.data.userName,
          refreshToken: '',
          useRefreshTokens: false,
          settings: { firstCampaign: true }
        });
      });

      deferred.resolve(response);

    }, (err) => {
      this.logOut();
      deferred.reject(err);
    });

    return deferred.promise;

  }

  setPassword(data) {
    const url = this.serviceBase + 'account/confirmEmail';
    return this.$http.post(url, data);
  }

  resetPassword(data) {
    const url = this.serviceBase + 'account/resetPassword';
    return this.$http.post(url, data);
  }

  requestPasswordReset(data) {
    const url = this.serviceBase + 'account/forgotPassword';
    return this.$http.post(url, data);
  }

  isISuper() {
    const url = this.serviceBase + 'uiUserSettings/isisuper';
    return this.$http.post(url, '');
  }

  setAuthDataCache(data) {
    this.cacheService.set(this._authDataCacheKey, data, {
      persistence: true
    });
  }
}